import Swiper from 'swiper';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';

const HERO_SLIDE_DELAY = 4000;

const loadVimeoPlayerApi = () => {
    if (window.Vimeo && window.Vimeo.Player) {
        return Promise.resolve(window.Vimeo.Player);
    }

    if (window.__mowgliVimeoPlayerPromise) {
        return window.__mowgliVimeoPlayerPromise;
    }

    window.__mowgliVimeoPlayerPromise = new Promise((resolve, reject) => {
        const existingScript = document.querySelector('script[data-vimeo-player-api]');

        if (existingScript) {
            existingScript.addEventListener('load', () => resolve(window.Vimeo.Player), { once: true });
            existingScript.addEventListener('error', reject, { once: true });
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://player.vimeo.com/api/player.js';
        script.async = true;
        script.dataset.vimeoPlayerApi = 'true';
        script.addEventListener('load', () => resolve(window.Vimeo.Player), { once: true });
        script.addEventListener('error', reject, { once: true });
        document.head.appendChild(script);
    });

    return window.__mowgliVimeoPlayerPromise;
};

const setHeroPaginationProgress = (swiper, activeIndex, progress = 0) => {
    if (!swiper.pagination || !swiper.pagination.bullets) {
        return;
    }

    swiper.pagination.bullets.forEach((bulletEl, bulletIndex) => {
        let bulletProgress = 0;

        if (bulletIndex < activeIndex) {
            bulletProgress = 1;
        } else if (bulletIndex === activeIndex) {
            bulletProgress = progress;
        }

        bulletEl.style.setProperty('--hero-pagination-progress', `${Math.max(0, Math.min(1, bulletProgress))}`);
    });
};

const initHeroSlider = async (heroSlider) => {
    if (!heroSlider) {
        return;
    }

    const videoIframes = Array.from(heroSlider.querySelectorAll('.js-hero-slide-video'));
    const videoPlayers = new WeakMap();
    let VimeoPlayerClass = null;

    const getOrCreateVideoPlayer = (iframeEl) => {
        const existingPlayer = videoPlayers.get(iframeEl);

        if (existingPlayer) {
            return existingPlayer;
        }

        if (!VimeoPlayerClass || !iframeEl) {
            return null;
        }

        const player = new VimeoPlayerClass(iframeEl);
        videoPlayers.set(iframeEl, player);

        return player;
    };

    if (videoIframes.length) {
        videoIframes.forEach((iframeEl) => {
            iframeEl.addEventListener(
                'load',
                () => window.setTimeout(() => {
                    iframeEl.classList.add('is-loaded');

                    const slide = iframeEl.closest('.swiper-slide');
                    const placeholder = slide ? slide.querySelector('.iframe-placeholder') : null;

                    if (placeholder) {
                        placeholder.style.display = 'none';
                    }
                }, 2000),
                { once: true }
            );
        });
        try {
            VimeoPlayerClass = await loadVimeoPlayerApi();

            videoIframes.forEach((iframeEl) => {
                getOrCreateVideoPlayer(iframeEl);
            });
        } catch (error) {
            console.error('Unable to load Vimeo Player API for the hero slider.', error);
        }
    }

    const heroSwiper = new Swiper(heroSlider, {
        direction: 'horizontal',
        modules: [Pagination, Navigation],
        rewind: true,
        slidesPerView: 1,
        pagination: {
            el: '.js-hero-slider .swiper-pagination',
            clickable: true,
            renderBullet: (index, className) => `
                <button class="${className} hero-slider__pagination-bullet" type="button" aria-label="Go to hero slide ${index + 1}">
                    <span class="hero-slider__pagination-track">
                        <span class="hero-slider__pagination-fill"></span>
                    </span>
                </button>
            `,
        },
        navigation: {
            nextEl: '.js-hero-slider .swiper-button-next',
            prevEl: '.js-hero-slider .swiper-button-prev',
        }
    });

    const hasSingleHeroSlide = heroSwiper.slides.length === 1;

    let activeTimerId = null;
    let activeFrameId = null;
    let activeVideoBindings = null;
    let activationFrameId = null;
    let activationRequestId = 0;

    const getActiveSlideIndex = () => {
        const activeSlide = heroSlider.querySelector('.swiper-slide-active');

        if (!activeSlide) {
            return heroSwiper.activeIndex;
        }

        return Array.from(heroSwiper.slides).indexOf(activeSlide);
    };

    const clearActiveSlideState = async () => {
        if (activeTimerId) {
            window.clearTimeout(activeTimerId);
            activeTimerId = null;
        }

        if (activeFrameId) {
            window.cancelAnimationFrame(activeFrameId);
            activeFrameId = null;
        }

        if (!activeVideoBindings) {
            return;
        }

        const { player, handleTimeUpdate, handleEnded } = activeVideoBindings;
        activeVideoBindings = null;

        player.off('timeupdate', handleTimeUpdate);
        player.off('ended', handleEnded);

        try {
            await player.pause();
        } catch (error) {
            // Ignore state errors when the slide changes faster than the player updates.
        }
    };

    const startTimedSlideProgress = (activeIndex, duration = HERO_SLIDE_DELAY) => {
        const startedAt = performance.now();

        const animate = (now) => {
            const elapsed = now - startedAt;
            const progress = elapsed / duration;

            setHeroPaginationProgress(heroSwiper, activeIndex, progress);

            if (progress < 1) {
                activeFrameId = window.requestAnimationFrame(animate);
            }
        };

        setHeroPaginationProgress(heroSwiper, activeIndex, 0);
        activeFrameId = window.requestAnimationFrame(animate);
        activeTimerId = window.setTimeout(() => {
            setHeroPaginationProgress(heroSwiper, activeIndex, 1);
            heroSwiper.slideNext();
        }, duration);
    };

    const startActiveSlide = async () => {
        const requestId = ++activationRequestId;
        const activeIndex = getActiveSlideIndex();
        const activeSlide = heroSwiper.slides[activeIndex];
        const activeVideoIframe = activeSlide ? activeSlide.querySelector('.js-hero-slide-video') : null;

        await clearActiveSlideState();

        if (requestId !== activationRequestId) {
            return;
        }

        const activeVideoPlayer = activeVideoIframe ? getOrCreateVideoPlayer(activeVideoIframe) : null;
        const shouldLoopSingleVideo = hasSingleHeroSlide && Boolean(activeVideoPlayer);

        if (!activeVideoPlayer) {
            startTimedSlideProgress(activeIndex);
            return;
        }

        setHeroPaginationProgress(heroSwiper, activeIndex, 0);

        const handleTimeUpdate = ({ seconds = 0, duration = 0 }) => {
            if (requestId !== activationRequestId || getActiveSlideIndex() !== activeIndex) {
                return;
            }

            const progress = duration > 0 ? seconds / duration : 0;
            setHeroPaginationProgress(heroSwiper, activeIndex, progress);
        };

        const handleEnded = () => {
            if (requestId !== activationRequestId || getActiveSlideIndex() !== activeIndex) {
                return;
            }

            if (shouldLoopSingleVideo) {
                setHeroPaginationProgress(heroSwiper, activeIndex, 0);
                return;
            }

            setHeroPaginationProgress(heroSwiper, activeIndex, 1);
            heroSwiper.slideNext();
        };

        activeVideoBindings = {
            player: activeVideoPlayer,
            handleTimeUpdate,
            handleEnded,
        };

        activeVideoPlayer.on('timeupdate', handleTimeUpdate);
        activeVideoPlayer.on('ended', handleEnded);

        try {
            await activeVideoPlayer.ready();

            if (requestId !== activationRequestId || getActiveSlideIndex() !== activeIndex) {
                return;
            }

            await activeVideoPlayer.setCurrentTime(0);

            if (requestId !== activationRequestId || getActiveSlideIndex() !== activeIndex) {
                return;
            }

            await activeVideoPlayer.play();
        } catch (error) {
            startTimedSlideProgress(activeIndex);
        }
    };

    const queueStartActiveSlide = () => {
        if (activationFrameId) {
            window.cancelAnimationFrame(activationFrameId);
        }

        activationFrameId = window.requestAnimationFrame(() => {
            activationFrameId = window.requestAnimationFrame(() => {
                activationFrameId = null;
                startActiveSlide();
            });
        });
    };

    heroSwiper.on('init', () => {
        setHeroPaginationProgress(heroSwiper, getActiveSlideIndex(), 0);
    });

    heroSwiper.on('slideChangeTransitionStart', () => {
        activationRequestId += 1;

        if (activationFrameId) {
            window.cancelAnimationFrame(activationFrameId);
            activationFrameId = null;
        }

        clearActiveSlideState();
        setHeroPaginationProgress(heroSwiper, getActiveSlideIndex(), 0);
    });

    heroSwiper.on('slideChangeTransitionEnd', () => {
        queueStartActiveSlide();
    });

    heroSwiper.on('paginationUpdate', () => {
        setHeroPaginationProgress(heroSwiper, getActiveSlideIndex(), 0);
    });

    setHeroPaginationProgress(heroSwiper, getActiveSlideIndex(), 0);
    queueStartActiveSlide();
};

document.addEventListener('DOMContentLoaded', async () => {
    
    
    const cardSliderEl = document.querySelector('.js-card-slider');
    new Swiper(cardSliderEl, {
        modules: [Autoplay, Pagination, Navigation],
        loop: true,
        slidesPerView: 1,
        effect: "fade",
        autoplay: {
            delay: 4000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
        },
        pagination: {
            el: '.js-card-slider .swiper-pagination',
            clickable: true,
        },
        navigation: {
            nextEl: ".swiper-button-next",
        }
    });

    

// Home page hero slider
    const heroSlider = document.querySelector('.js-hero-slider');
    await initHeroSlider(heroSlider);


// Basic image slider

    const basicImageSliders = document.querySelectorAll('.js-basic-image-slider');
    basicImageSliders.forEach((basicImageSlider) => {
        new Swiper(basicImageSlider, {
           
            breakpoints: {
                0: {
                    slidesPerView: 'auto',
                
                    spaceBetween: 17,
                    
                },
                780: {
                        slidesPerView: 'auto',
                    spaceBetween: 17,
                    
                },
                1620: {
                        slidesPerView: 'auto',
                    spaceBetween: 17,
                    
                },
            },
            autoplay: {
                delay: 4000,
                disableOnInteraction: false,
            },  
            modules: [Pagination],
            pagination: {
                el: '.js-basic-image-slider .swiper-pagination',
                clickable: true,
            },
            // navigation: {
            //     nextEl: ".js-basic-image-slider .swiper-button-next",
            //     prevEl: ".js-basic-image-slider .swiper-button-prev",
            // },
        });
    });

    
// Menu slider
    const menuLinkSlider = document.querySelector('.js-menu-link-slider');

    if (menuLinkSlider) {
        new Swiper(menuLinkSlider, {
            loop: false,
            // centeredSlides: true,
            modules: [Pagination, Navigation],
            
            pagination: {
                el: '.js-menu-link-slider .swiper-pagination',
                clickable: true,
            },
            navigation: {
                nextEl: ".menus-slider-nav .swiper-button-next",
                prevEl: ".menus-slider-nav .swiper-button-prev",
            },
            breakpoints: {
                0: {
                    slidesPerView: 1.2,
                    spaceBetween: 17,
                    slidesOffsetBefore: 0,
                    slidesOffsetAfter: 0,
                    centeredSlides: true,
                },
                780: {
                    slidesPerView: 3.8,
                    spaceBetween: 17,
                    slidesOffsetBefore: 20,
                    slidesOffsetAfter: 20,
                },
                1620: {
                    slidesPerView: 5,
                    spaceBetween: 17,
                    slidesOffsetBefore: 40,
                    slidesOffsetAfter: 40,
                },
            }
        });
    }

// Menu slide hover videos
    const menuSlidesWithVideo = document.querySelectorAll('.js-menu-link-slider .swiper-slide[data-vimeo-id]');

    if (menuSlidesWithVideo.length) {
        const menuVideoPlayers = new WeakMap();
        let activeMenuSlide = null;

        const getOrCreateMenuVideoPlayer = async (slide) => {
            if (menuVideoPlayers.has(slide)) {
                return menuVideoPlayers.get(slide);
            }

            const VimeoPlayer = await loadVimeoPlayerApi();
            const vimeoId = slide.dataset.vimeoId;

            const container = document.createElement('div');
            container.className = 'menu-slide__video';
            slide.insertBefore(container, slide.firstChild);

            const player = new VimeoPlayer(container, {
                id: Number(vimeoId),
                autoplay: false,
                muted: true,
                loop: true,
                controls: false,
                byline: false,
                title: false,
                portrait: false,
                playsinline: true,
                dnt: true,
            });

            await player.ready();
            menuVideoPlayers.set(slide, player);
            return player;
        };

        const activateMenuSlide = async (slide) => {
            if (activeMenuSlide && activeMenuSlide !== slide) {
                activeMenuSlide.classList.remove('has-menu-video-active');
                if (menuVideoPlayers.has(activeMenuSlide)) {
                    try {
                        await menuVideoPlayers.get(activeMenuSlide).pause();
                    } catch (e) {
                        // ignore
                    }
                }
            }

            activeMenuSlide = slide;

            try {
                const player = await getOrCreateMenuVideoPlayer(slide);
                // Guard: another slide may have been activated while we awaited
                if (activeMenuSlide === slide) {
                    slide.classList.add('has-menu-video-active');
                    await player.play();
                }
            } catch (e) {
                // Player unavailable — fall back to static image
            }
        };

        // Autoplay the first slide with a video on load
        activateMenuSlide(menuSlidesWithVideo[0]);

        menuSlidesWithVideo.forEach((slide) => {
            slide.addEventListener('mouseenter', () => {
                activateMenuSlide(slide);
            });
        });
    }
    

// Restaurant slider
    const restaurantSlider = document.querySelector('.js-restaurant-slider');
    new Swiper(restaurantSlider, {
        modules: [Pagination, Navigation],
        loop: false,
        slidesPerView: 1,
        pagination: {
            el: '.js-restaurant-slider .swiper-pagination',
            clickable: true,
        },
        navigation: {
            nextEl: ".js-restaurant-slider .swiper-button-next",
            prevEl: ".js-restaurant-slider .swiper-button-prev",
        },
        
    });
        

// Feature cards slider (page_sections feature_cards with more than 4 cards)
    const featureCardsSliders = document.querySelectorAll('.js-feature-cards-slider');
    featureCardsSliders.forEach((featureCardsSlider) => {
        new Swiper(featureCardsSlider, {
            modules: [Pagination],
            loop: false,
            spaceBetween: 16,
            breakpoints: {
                0: { slidesPerView: 1.2 },
                700: { slidesPerView: 2.4 },
                1200: { slidesPerView: 4 },
            },
            pagination: {
                el: featureCardsSlider.querySelector('.swiper-pagination'),
                clickable: true,
            },
        });
    });


// Menus Page menu-item slider
    const menuItemSliders = document.querySelectorAll('.js-menu-item-slider');
    menuItemSliders.forEach((menuItemSlider) => {
        const nextButton = menuItemSlider.querySelector('.swiper-button-next');
        const prevButton = menuItemSlider.querySelector('.swiper-button-prev');

        new Swiper(menuItemSlider, {
            loop: false,
            modules: [Navigation],
            breakpoints: {
                0: {
                    slidesPerView: 1.3,
                    spaceBetween: 17,
                    slidesOffsetBefore: 38,
                    slidesOffsetAfter: 38,
                    centeredSlides: false,
                    
                },
                800: {
                    slidesPerView: 3.38,
                    spaceBetween: 17,
                    slidesOffsetBefore: 17,
                    slidesOffsetAfter: 17,
                },
                1620: {
                    slidesPerView: 4.2,
                    spaceBetween: 17,
                    slidesOffsetBefore: 0,
                    slidesOffsetAfter: 17,
                },
            },
            
            // pagination: {
            //     el: '.js-menu-item-slider .swiper-pagination',
            //     clickable: true,
            // },
            navigation: {
                nextEl: nextButton,
                prevEl: prevButton,
            },
        });
    }); 



// Other news slider
    const otherNewsSlider = document.querySelector('.js-other-news-slider');

    new Swiper(otherNewsSlider, {
                slidesPerView: 3.4,
                spaceBetween: 17,
                loop: false,
                // modules: [Navigation],
                // pagination: {
                //     el: otherNewsSlider.querySelector('.swiper-pagination'),
                //     clickable: true
                // },
                // navigation: {
                //     nextEl: '.js-other-news-slider .swiper-button-next',
                //     prevEl: '.js-other-news-slider .swiper-button-prev'
                // },
                breakpoints: {
                0: {
                    slidesPerView: 1,
                    spaceBetween: 17,
                    slidesOffsetBefore: 0,
                    slidesOffsetAfter: 0,
                    centeredSlides: false,
                    
                },
                800: {
                    slidesPerView: 3.38,
                    spaceBetween: 17,
                    slidesOffsetBefore: 17,
                    slidesOffsetAfter: 17,
                },
                1620: {
                    slidesPerView: 4.2,
                    spaceBetween: 17,
                    slidesOffsetBefore: 17,
                    slidesOffsetAfter: 17,
                },
            },
            });


// News post slider
    const newsSlider = document.querySelector('.js-news-slider');
    new Swiper(newsSlider, {
        loop: false,
        modules: [Navigation],
        breakpoints: {
            0: {
                slidesPerView: 1.3,
                spaceBetween: 17,
                slidesOffsetBefore: 17,
                slidesOffsetAfter: 17,
                centeredSlides: true,
                
            },
            700: {
                slidesPerView: 3.8,
                spaceBetween: 17,
                slidesOffsetBefore: 17,
                slidesOffsetAfter: 17,
            },
            1620: {
                slidesPerView: 5.5,
                spaceBetween: 17,
                slidesOffsetBefore: 40,
                slidesOffsetAfter: 40,
            },
        },
        
        pagination: {
            el: '.js-news-slider .swiper-pagination',
            clickable: true,
        },
        navigation: {
            nextEl: ".news-slider-nav .swiper-button-next",
            prevEl: ".news-slider-nav .swiper-button-prev",
        },
    }); 




    
// Shop sliders
    document.querySelectorAll('.js-product-row-slider').forEach((rowEl) => {
        const section = rowEl.closest('.section__product-row');
        new Swiper(rowEl, {
            modules: [Navigation],
            loop: false,
            slidesPerView: 5,
            spaceBetween: 32,
            navigation: section ? {
                nextEl: section.querySelector('.swiper-button-next'),
                prevEl: section.querySelector('.swiper-button-prev'),
            } : false,
            breakpoints: {
                0: {
                    slidesPerView: 1.6,
                    spaceBetween: 17,
                },
                600: {
                    slidesPerView: 3.2,
                    spaceBetween: 17,
                },
                992: {
                    slidesPerView: 4.5,
                    spaceBetween: 24,
                },
                1400: {
                    slidesPerView: 5,
                    spaceBetween: 32,
                },
            },
        });
    });

// Single-restaurant page sliders
    const restaurantHeroEl = document.querySelector('.js-restaurant-hero-swiper');
    if (restaurantHeroEl) {
        new Swiper(restaurantHeroEl, {
            modules: [Pagination, Autoplay],
            loop: true,
            slidesPerView: 1,
            autoplay: {
                delay: 4500,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
            },
            pagination: {
                el: '.js-restaurant-hero-pagination',
                clickable: true,
            },
        });
    }

    const staffFavouritesEl = document.querySelector('.js-staff-favourites-swiper');
    if (staffFavouritesEl) {
        new Swiper(staffFavouritesEl, {
            modules: [Navigation],
            loop: false,
            slidesPerView: 1,
            spaceBetween: 0,
            navigation: {
                nextEl: '.js-staff-favourites-next',
                prevEl: '.js-staff-favourites-prev',
            },
        });
    }

    const cultureMusicEl = document.querySelector('.js-culture-music-swiper');
    if (cultureMusicEl) {
        new Swiper(cultureMusicEl, {
            modules: [Navigation],
            loop: false,
            slidesPerView: 1,
            spaceBetween: 0,
            navigation: {
                nextEl: '.js-culture-music-next',
                prevEl: '.js-culture-music-prev',
            },
        });
    }

    const apparelSlider = document.querySelector('.js-apparel-slider');
    new Swiper(apparelSlider, {
        loop: false,
        slidesPerView: 4.5,
        spaceBetween: 17,
        slidesOffsetBefore: 40,
        autoplay: {
            delay: 4000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
        },
        pagination: {
            el: '.js-apparel-slider .swiper-pagination',
            clickable: true,
        },

        breakpoints: {
            0: {
                slidesPerView: 1.3,
                spaceBetween: 17,
                slidesOffsetBefore: 10,
                slidesOffsetAfter: 10,
                centeredSlides: true,
                
            },
            600: {
                slidesPerView: 3.8,
                spaceBetween: 17,
                slidesOffsetBefore: 20,
                slidesOffsetAfter: 40,
            },
            1620: {
                slidesPerView: 5.5,
                spaceBetween: 17,
                slidesOffsetBefore: 40,
                slidesOffsetAfter: 40,
            },
        },


    });

});


